import { LegendParameters } from '@components/mainmap/legend/LegendParameters'
import { ScaleValue } from '@models/ScaleValue'

export abstract class StationVariable {
  abstract getLabel(): string | undefined
  abstract getIcon(): string | undefined
  abstract getDecimals(): number
  abstract getUnits(): string
  abstract getScaleColors(): ScaleValue[]

  getColorByValue(value?: number) {
    if (value === undefined) {
      return '#A0A3BD'
    }
    const scaleColors = this.getScaleColors()
    const scaleValue = scaleColors.find((scaleValue) => {
      if (scaleValue.min !== undefined && scaleValue.max !== undefined) {
        return value >= scaleValue.min && value < scaleValue.max
      } else if (scaleValue.min !== undefined) {
        return value >= scaleValue.min
      } else if (scaleValue.max !== undefined) {
        return value < scaleValue.max
      }
    })

    return scaleValue ? scaleValue.value : '#FFFFFF'
  }

  getLegendParameters(): LegendParameters {
    return ScaleValue.toLegendParameters(this.getScaleColors(), this.getUnits())
  }

  static readonly ScaleValues_Temperature: ScaleValue[] = [
    { max: -42, value: '#ff6eff' },
    { min: -42, max: -39, value: '#f627eb' },
    { min: -39, max: -36, value: '#b414b9' },
    { min: -36, max: -33, value: '#960ca3' },
    { min: -33, max: -30, value: '#78048d' },
    { min: -30, max: -27, value: '#57007f' },
    { min: -27, max: -24, value: '#4b007f' },
    { min: -24, max: -21, value: '#32007f' },
    { min: -21, max: -18, value: '#00327f' },
    { min: -18, max: -15, value: '#00467f' },
    { min: -15, max: -12, value: '#0062af' },
    { min: -12, max: -10, value: '#0072cf' },
    { min: -10, max: -8, value: '#1392ff' },
    { min: -8, max: -6, value: '#49acff' },
    { min: -6, max: -4, value: '#6dbcff' },
    { min: -4, max: -2, value: '#a3d4ff' },
    { min: -2, max: 0, value: '#c7e4ff' },
    { min: 0, max: 2, value: '#FFFFFF' },
    { min: 2, max: 4, value: '#b1f1d6' },
    { min: 4, max: 6, value: '#87d3ab' },
    { min: 6, max: 8, value: '#4a9775' },
    { min: 8, max: 10, value: '#08b30f' },
    { min: 10, max: 12, value: '#39c20c' },
    { min: 12, max: 15, value: '#84d908' },
    { min: 15, max: 18, value: '#b5e805' },
    { min: 18, max: 21, value: '#cef003' },
    { min: 21, max: 24, value: '#f3fb01' },
    { min: 24, max: 27, value: '#f4d90b' },
    { min: 27, max: 30, value: '#f4bd0b' },
    { min: 30, max: 33, value: '#f4880b' },
    { min: 33, max: 36, value: '#f4520b' },
    { min: 36, max: 39, value: '#dc2708' },
    { min: 39, max: 42, value: '#ba130f' },
    { min: 42, max: 45, value: '#8c0000' },
    { min: 45, max: 48, value: '#640000' },
    { min: 48, value: '#b46464' }
  ]
  static readonly ScaleValues_Humidity: ScaleValue[] = [
    { min: 0, max: 0.1, value: '#FFFFFF' },
    { min: 0.1, max: 2.5, value: '#f4f4f4' },
    { min: 2.5, max: 5, value: '#72121b' },
    { min: 5, max: 10, value: '#900c3f' },
    { min: 10, max: 15, value: '#a50021' },
    { min: 15, max: 20, value: '#fc0404' },
    { min: 20, max: 25, value: '#ff6d33' },
    { min: 25, max: 30, value: '#ffaf33' },
    { min: 30, max: 35, value: '#ffdd4b' },
    { min: 35, max: 40, value: '#ffff66' },
    { min: 40, max: 50, value: '#ccff33' },
    { min: 50, max: 60, value: '#66ff33' },
    { min: 60, max: 70, value: '#99ff99' },
    { min: 70, max: 80, value: '#66ffff' },
    { min: 80, max: 90, value: '#66ccff' },
    { min: 90, max: 95, value: '#3399ff' },
    { min: 95, max: 100, value: '#0000cc' }
  ]
  static readonly ScaleValues_WindSpeed: ScaleValue[] = [
    { min: 0, max: 2.5, value: '#ffffff' },
    { min: 2.5, max: 5, value: '#97e6ff' },
    { min: 5, max: 10, value: '#33ccff' },
    { min: 10, max: 15, value: '#0099ff' },
    { min: 15, max: 20, value: '#00ff99' },
    { min: 20, max: 25, value: '#33cc66' },
    { min: 25, max: 30, value: '#66cc33' },
    { min: 30, max: 35, value: '#66ff00' },
    { min: 35, max: 40, value: '#a4f22f' },
    { min: 40, max: 45, value: '#b7cf0e' },
    { min: 45, max: 50, value: '#d6f017' },
    { min: 50, max: 55, value: '#cc9900' },
    { min: 55, max: 60, value: '#ff9900' },
    { min: 60, max: 65, value: '#ff9966' },
    { min: 65, max: 70, value: '#cc9999' },
    { min: 70, max: 75, value: '#cc6633' },
    { min: 75, max: 80, value: '#cc3333' },
    { min: 80, max: 85, value: '#ff0d0d' },
    { min: 85, max: 90, value: '#c60000' },
    { min: 90, max: 100, value: '#800000' },
    { min: 100, max: 110, value: '#800050' },
    { min: 110, max: 120, value: '#a00077' },
    { min: 120, max: 130, value: '#cc00cc' },
    { min: 130, max: 140, value: '#ff00ff' },
    { min: 140, max: 150, value: '#ff80ff' },
    { min: 150, max: 160, value: '#ffa0ff' },
    { min: 160, max: 180, value: '#e3e3e3' },
    { min: 180, value: '#c6c6c6' }
  ]
  static readonly ScaleValues_Precipitation: ScaleValue[] = [
    { min: 0, max: 0.1, value: '#ffffff00' },
    { min: 0.1, max: 0.2, value: '#33ccff' },
    { min: 0.2, max: 0.5, value: '#0099ff' },
    { min: 0.5, max: 1, value: '#00ff99' },
    { min: 1, max: 2, value: '#33cc66' },
    { min: 2, max: 3, value: '#66cc33' },
    { min: 3, max: 4, value: '#66ff00' },
    { min: 4, max: 5, value: '#a4f22f' },
    { min: 5, max: 6, value: '#b7cf0e' },
    { min: 6, max: 7, value: '#d6f017' },
    { min: 7, max: 8, value: '#cc9900' },
    { min: 8, max: 9, value: '#ff9900' },
    { min: 9, max: 10, value: '#ff9966' },
    { min: 10, max: 15, value: '#cc9999' },
    { min: 15, max: 20, value: '#cc6633' },
    { min: 20, max: 25, value: '#cc3333' },
    { min: 25, max: 30, value: '#ff0d0d' },
    { min: 30, max: 40, value: '#c60000' },
    { min: 40, max: 50, value: '#800000' },
    { min: 50, max: 60, value: '#800050' },
    { min: 60, max: 70, value: '#a00077' },
    { min: 70, max: 100, value: '#cc00cc' },
    { min: 100, max: 200, value: '#ff00ff' },
    { min: 200, value: '#ff00ff' }
  ]
  static readonly ScaleValues_Pressure: ScaleValue[] = [
    { max: 900, value: '#ffffff' },
    { min: 900, max: 905, value: '#ff6eff' },
    { min: 905, max: 910, value: '#ff46f8' },
    { min: 910, max: 915, value: '#f627eb' },
    { min: 915, max: 920, value: '#d41dd1' },
    { min: 920, max: 925, value: '#c318c4' },
    { min: 925, max: 930, value: '#b414b9' },
    { min: 930, max: 935, value: '#a510ae' },
    { min: 935, max: 940, value: '#870898' },
    { min: 940, max: 945, value: '#64007f' },
    { min: 945, max: 950, value: '#3e007f' },
    { min: 950, max: 955, value: '#3200fe' },
    { min: 955, max: 960, value: '#0032fe' },
    { min: 960, max: 965, value: '#0048e1' },
    { min: 965, max: 970, value: '#005ec5' },
    { min: 970, max: 975, value: '#0074a9' },
    { min: 975, max: 980, value: '#008a8d' },
    { min: 980, max: 985, value: '#00a170' },
    { min: 985, max: 990, value: '#00b754' },
    { min: 990, max: 995, value: '#00cd38' },
    { min: 995, max: 1000, value: '#00e31c' },
    { min: 1000, max: 1005, value: '#00fa00' },
    { min: 1005, max: 1010, value: '#fefe00' },
    { min: 1010, max: 1015, value: '#fee100' },
    { min: 1015, max: 1020, value: '#fec800' },
    { min: 1020, max: 1025, value: '#feaf00' },
    { min: 1025, max: 1030, value: '#fe9600' },
    { min: 1030, max: 1035, value: '#e67d00' },
    { min: 1035, max: 1040, value: '#e66400' },
    { min: 1040, max: 1045, value: '#dc4b1e' },
    { min: 1045, max: 1050, value: '#c8321e' },
    { min: 1050, max: 1055, value: '#b4191e' },
    { min: 1055, max: 1060, value: '#aa001e' },
    { min: 1060, value: '#aa001e' }
  ]
}
